import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import router from '@/router'

interface User {
  id: string
  name: string
  email: string
  emailVerified: boolean
  hasProjects?: boolean
}

interface ApiResponse<T = any> {
  success: boolean
  message: string
  data?: T
  errors?: any[]
  code?: string
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const pendingLogin = ref<{ email: string; password: string } | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const isVerified = computed(() => user.value?.emailVerified || false)

  // API Base URL
  const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'https://api.revisit.pro'

  console.log('API_BASE_URL', API_BASE_URL)

  // Track refresh attempts to prevent infinite loops
  let refreshInProgress = false

  const attemptTokenRefresh = async (): Promise<boolean> => {
    if (refreshInProgress) return false
    refreshInProgress = true
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, { 
        method: 'POST', 
        credentials: 'include', 
        headers: { 'Content-Type': 'application/json' } 
      })
      return response.ok
    } catch {
      return false
    } finally {
      refreshInProgress = false
    }
  }

  // Helper function for API requests
  const apiRequest = async <T = any>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> => {
    const url = `${API_BASE_URL}${endpoint}`
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    const method = (options.method || 'GET').toUpperCase()
    const isAuthEndpoint = endpoint.startsWith('/auth/')

    // Inject CSRF header for unsafe methods if present
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && !isAuthEndpoint) {
      try {
        let tokenCookie = ''
        try { tokenCookie = document.cookie.match(/(?:^|; )rv_csrf=([^;]+)/)?.[1] || '' } catch {}
        if (!tokenCookie) {
          // Attempt to refresh to obtain CSRF cookie
          try { await attemptTokenRefresh() } catch {}
          try { tokenCookie = document.cookie.match(/(?:^|; )rv_csrf=([^;]+)/)?.[1] || '' } catch {}
        }
        if (tokenCookie) defaultHeaders['X-CSRF-Token'] = decodeURIComponent(tokenCookie)
      } catch {}
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      credentials: 'include',
    }

    const makeRequest = async (): Promise<ApiResponse<T>> => {
      try {
        const response = await fetch(url, config)
        const data = await response.json()
        return data
      } catch (err) {
        throw new Error(`Network error: ${err instanceof Error ? err.message : 'Unknown error'}`)
      }
    }

    // Make initial request
    let result: ApiResponse<T>
    try {
      result = await makeRequest()
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : 'Network error'
      }
    }

    // If request failed with auth error and it's not an auth endpoint, try to refresh token and retry
    if (!result.success && !isAuthEndpoint && (
      result.message?.includes('Please log in') || 
      result.message?.includes('Invalid token') ||
      result.message?.includes('Token expired') ||
      result.message?.includes('Session revoked')
    )) {
      const refreshSuccess = await attemptTokenRefresh()
      if (refreshSuccess) {
        // Update CSRF token after refresh
        try {
          const newTokenCookie = document.cookie.match(/(?:^|; )rv_csrf=([^;]+)/)?.[1] || ''
          if (newTokenCookie) {
            config.headers = {
              ...config.headers,
              'X-CSRF-Token': decodeURIComponent(newTokenCookie)
            }
          }
        } catch {}
        
        // Retry the request
        try {
          result = await makeRequest()
        } catch (err) {
          return {
            success: false,
            message: err instanceof Error ? err.message : 'Network error'
          }
        }
      } else {
        // If refresh failed, clear user state and return auth error
        user.value = null
        stopBackgroundRefresh()
        return {
          success: false,
          message: 'Session expired. Please log in again.',
          code: 'SESSION_EXPIRED'
        }
      }
    }

    return result
  }

  // Actions
  const register = async (userData: {
    name: string
    email: string
    password: string
    invitationToken?: string
  }) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest<{ user: User; requiresFirstProject: boolean }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      // Set user from response (auto-login)
      if (response.data?.user) {
        user.value = response.data.user
        
        // Start background refresh after successful registration/login
        startBackgroundRefresh()
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Registration failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const login = async (credentials: { email: string; password: string }) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest<{ user: User }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      })

      if (!response.success) {
        if (response.code === 'MFA_REQUIRED') {
          pendingLogin.value = { email: credentials.email, password: credentials.password }
          const redirect = router.currentRoute.value.query.redirect as string | undefined
          router.push({ name: 'mfa', query: redirect ? { redirect } : {} })
          return response
        }
        throw new Error(response.message)
      }

      // Cookies carry auth; just store user
      user.value = response.data!.user
      
      // Start background refresh after successful login
      startBackgroundRefresh()

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const verifyMfa = async (code: string, opts?: { rememberDevice?: boolean }) => {
    if (!pendingLogin.value) throw new Error('No pending login')
    isLoading.value = true
    error.value = null
    try {
      const body: any = { email: pendingLogin.value.email, password: pendingLogin.value.password, mfa: code }
      if (opts?.rememberDevice) body.rememberDevice = true
      const response = await apiRequest<{ user: User }>('/auth/login', { method: 'POST', body: JSON.stringify(body) })
      if (!response.success) throw new Error(response.message || 'MFA verification failed')
      user.value = response.data!.user
      pendingLogin.value = null
      
      // Start background refresh after successful MFA verification
      startBackgroundRefresh()
      
      // Navigate to redirect if any
      const redirect = router.currentRoute.value.query.redirect as string | undefined
      router.push(redirect || '/projects')
      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'MFA verification failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const listPendingInvitations = async () => {
    return apiRequest('/api/invitations/pending', { method: 'GET' })
  }

  const logout = async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' })
    } catch {}
    user.value = null
    error.value = null
    
    // Stop background refresh on logout
    stopBackgroundRefresh()
  }

  const getGoogleLoginUrl = async (): Promise<string> => {
    try {
      const res = await apiRequest<{ url: string }>(`/auth/google/start`, { method: 'GET' })
      if (!res.success || !(res as any).url && !(res.data as any)?.url) {
        throw new Error(res.message || 'Failed to start Google login')
      }
      // Support both { url } and { data: { url } }
      const url = ((res as any).url as string) || ((res.data as any)?.url as string)
      return url
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to start Google login')
    }
  }

  const verifyEmail = async (verificationToken: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest<{ user: User }>('/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token: verificationToken }),
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      // Set user from response (auto-login after verification)
      if (response.data?.user) {
        user.value = response.data.user as any
        
        // Start background refresh after successful verification/login
        startBackgroundRefresh()
      } else if (user.value) {
        // Fallback: optimistically mark the user as verified locally
        user.value = { ...(user.value as any), emailVerified: true }
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Email verification failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const resendVerification = async (email: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest('/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to resend verification email'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateProfileName = async (name: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await apiRequest<{ user: User }>(`/api/user`, {
        method: 'PATCH',
        body: JSON.stringify({ name })
      })
      if (!response.success) {
        throw new Error(response.message)
      }
      user.value = response.data!.user
      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Update failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await apiRequest(`/auth/change-password`, {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword })
      })
      if (!response.success) {
        throw new Error(response.message)
      }
      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Password change failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const forgotPassword = async (email: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to send password reset email'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (resetToken: string, password: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token: resetToken, password }),
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Password reset failed'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const fetchUser = async () => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest<{ user: User }>('/api/user')

      if (!response.success) {
        // If fetch fails after retry attempts, likely not authenticated
        user.value = null
        throw new Error(response.message)
      }

      user.value = response.data!.user as any
      
      // If user is not verified, fetch verification status to check if they have projects
      if (user.value && !user.value.emailVerified) {
        try {
          const statusRes = await apiRequest<{ emailVerified: boolean; hasProjects: boolean; requiresVerification: boolean }>('/auth/verification-status')
          if (statusRes.success && statusRes.data) {
            user.value.hasProjects = statusRes.data.hasProjects
          }
        } catch (err) {
          console.error('Failed to fetch verification status:', err)
        }
      }
      
      return response
    } catch (err) {
      // If fetch fails, likely not authenticated
      user.value = null
      error.value = err instanceof Error ? err.message : 'Failed to fetch user data'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  // Session management
  const listLoginSessions = async (): Promise<{ sessions: any[]; currentSid?: string }> => {
    const res = await apiRequest<{ sessions: any[]; currentSid?: string }>(`/auth/sessions`, { method: 'GET' })
    if (!res.success) throw new Error(res.message || 'Failed to fetch sessions')
    return (res.data as any) || { sessions: [], currentSid: undefined }
  }

  const revokeLoginSession = async (id: string): Promise<void> => {
    const res = await apiRequest(`/auth/sessions/${encodeURIComponent(id)}/revoke`, { method: 'POST' })
    if (!res.success) throw new Error(res.message || 'Failed to revoke session')
  }

  const revokeOtherLoginSessions = async (): Promise<void> => {
    const res = await apiRequest(`/auth/sessions/revoke-others`, { method: 'POST' })
    if (!res.success) throw new Error(res.message || 'Failed to revoke other sessions')
  }

  // Background token refresh interval
  let refreshInterval: ReturnType<typeof setInterval> | null = null

  const startBackgroundRefresh = () => {
    // Clear existing interval
    if (refreshInterval) {
      clearInterval(refreshInterval)
    }

    // Refresh token every 90 minutes (before 2-hour expiry)
    refreshInterval = setInterval(async () => {
      if (user.value) { // Only refresh if user is logged in
        try {
          await attemptTokenRefresh()
        } catch (error) {
          console.warn('Background token refresh failed:', error)
        }
      }
    }, 90 * 60 * 1000) // 90 minutes

    // Also refresh when page becomes visible (user returns to tab)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && user.value) {
        try {
          await attemptTokenRefresh()
        } catch (error) {
          console.warn('Visibility change token refresh failed:', error)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    // Store cleanup function
    ;(startBackgroundRefresh as any).cleanup = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }

  const stopBackgroundRefresh = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
    
    // Clean up visibility change listener
    if ((startBackgroundRefresh as any).cleanup) {
      ;(startBackgroundRefresh as any).cleanup()
    }
  }

  // Initialize store - fetch user if token exists
  const init = async () => {
    try {
      await fetchUser()
      // Start background refresh if user is authenticated
      if (user.value) {
        startBackgroundRefresh()
      }
    } catch {}
  }

  return {
    // State
    user,
    isLoading,
    error,
    pendingLogin,
    
    // Getters
    isAuthenticated,
    isVerified,
    
    // Actions
    register,
    login,
    verifyMfa,
    logout,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    updateProfileName,
    changePassword,
    fetchUser,
    clearError,
    init,
    getGoogleLoginUrl,
    listPendingInvitations,
    listLoginSessions,
    revokeLoginSession,
    revokeOtherLoginSessions,
  }
})